// store/unlockMilestone.ts — P4 unlock milestone state per milestone_id.
//
// FSM: docs/contracts/aha/state_machines/unlock_milestone.yaml.
// 4 instances run in parallel: first_council / ten / fifty / hundred.
import { create } from 'zustand';

export type MilestoneId =
  | 'first_council'
  | 'ten_councils'
  | 'fifty_councils'
  | 'hundred_councils';

export const MILESTONE_THRESHOLDS: Record<MilestoneId, number> = {
  first_council: 1,
  ten_councils: 10,
  fifty_councils: 50,
  hundred_councils: 100,
};

interface MilestoneRecord {
  firedAt: string | null;
  dismissedAt: string | null;
}

interface UnlockMilestoneStore {
  records: Record<MilestoneId, MilestoneRecord>;
  hydrate: (snapshot: Partial<Record<MilestoneId, MilestoneRecord>>) => void;
  fire: (id: MilestoneId, ts?: string) => void;
  dismiss: (id: MilestoneId, ts?: string) => void;
  reset: () => void;
}

const empty: MilestoneRecord = { firedAt: null, dismissedAt: null };
const initial: Record<MilestoneId, MilestoneRecord> = {
  first_council: { ...empty },
  ten_councils: { ...empty },
  fifty_councils: { ...empty },
  hundred_councils: { ...empty },
};

export const useUnlockMilestone = create<UnlockMilestoneStore>((set) => ({
  records: { ...initial },
  hydrate: (snapshot) =>
    set((s) => {
      const next: Record<MilestoneId, MilestoneRecord> = { ...s.records };
      for (const k of Object.keys(snapshot) as MilestoneId[]) {
        const merged = { ...next[k], ...snapshot[k] };
        next[k] = merged;
      }
      return { records: next };
    }),
  fire: (id, ts) =>
    set((s) => ({
      records: {
        ...s.records,
        [id]: { ...s.records[id], firedAt: ts ?? new Date().toISOString() },
      },
    })),
  dismiss: (id, ts) =>
    set((s) => ({
      records: {
        ...s.records,
        [id]: { ...s.records[id], dismissedAt: ts ?? new Date().toISOString() },
      },
    })),
  reset: () => set({ records: { ...initial } }),
}));

export function shouldUnlock(councilsRun: number, id: MilestoneId): boolean {
  return councilsRun === MILESTONE_THRESHOLDS[id];
}
